import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch } from "../../../config/api";
import Loader from "../../../components/Loader";
import EmptyState from "../../../components/EmptyState";
import { getUser } from "../../../config/auth";
import FilterChip from "../../../components/FilterChip";
import { canSeeAudit } from "../../../config/rbac";
import Pagination from "../../../components/Pagination";
import PageHeader from "../../../components/PageHeader";

const PAGE_SIZE = 50;

function formatDateTime(dt) {
  if (!dt) return "—";
  return new Date(dt).toLocaleString("en-IN", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function shortId(id) {
  if (!id) return "—";
  return String(id).slice(0, 8);
}

const ACTION_COLORS = {
  Created: { bg: "#f0fdf4", color: "var(--success)" },
  Updated: { bg: "#eff6ff", color: "#2563eb" },
  Deleted: { bg: "#fff1f2", color: "var(--danger)" },
  StatusChanged: { bg: "rgba(8,145,178,0.08)", color: "var(--brand-hover)" },
  Closed: { bg: "#f0fdf4", color: "var(--success-text)" },
  Validated: { bg: "#f0fdf4", color: "var(--success)" },
  CommentAdded: { bg: "#f0f9ff", color: "#0284c7" },
  EvidenceUploaded: { bg: "#faf5ff", color: "#9333ea" },
};

function ActionBadge({ action }) {
  const key = Object.keys(ACTION_COLORS).find((k) => action?.includes(k)) || "";
  const colors = ACTION_COLORS[key] || {
    bg: "var(--surface-2)",
    color: "var(--text-2)",
  };
  return (
    <span
      style={{
        padding: "2px 9px",
        borderRadius: 999,
        fontSize: 12,
        fontWeight: 600,
        background: colors.bg,
        color: colors.color,
      }}
    >
      {action}
    </span>
  );
}

export default function AuditPage() {
  const me = getUser();
  const role = me?.role || "Staff";
  const allowed = canSeeAudit(role);
  const navigate = useNavigate();

  const [logs, setLogs] = useState([]);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [users, setUsers] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [page, setPage] = useState(1);
  const fetchIdRef = useRef(0);

  const [userFilter, setUserFilter] = useState("");
  const [deptFilter, setDeptFilter] = useState("");
  const [projectFilter, setProjectFilter] = useState("");
  const [entityTypeFilter, setEntityTypeFilter] = useState("");
  const [search, setSearch] = useState("");

  // Load dropdown data once
  useEffect(() => {
    if (!allowed) return;
    Promise.all([
      apiFetch("/api/users/assignable").then(r => r.ok ? r.json() : []).catch(() => []),
      apiFetch("/api/departments").then(r => r.ok ? r.json() : []).catch(() => []),
      apiFetch("/api/projects").then(r => r.ok ? r.json() : []).catch(() => []),
    ]).then(([u, d, p]) => {
      setUsers(Array.isArray(u) ? u : []);
      setDepartments(Array.isArray(d) ? d : []);
      setProjects(Array.isArray(p) ? p : []);
    });
  }, [allowed]);

  // Fetch logs from server whenever filters or page change
  useEffect(() => {
    if (!allowed) { setLoading(false); return; }

    const myId = ++fetchIdRef.current;
    setLoading(true);
    setError("");

    const params = new URLSearchParams({ page, limit: PAGE_SIZE });
    if (userFilter)      params.set("userId", userFilter);
    if (deptFilter)      params.set("deptId", deptFilter);
    if (entityTypeFilter) params.set("entityType", entityTypeFilter);
    if (projectFilter)   params.set("projectId", projectFilter);
    if (search.trim())   params.set("search", search.trim());

    apiFetch(`/api/audit/recent?${params}`)
      .then(r => { if (!r.ok) throw new Error("Failed to load"); return r.json(); })
      .then(data => {
        if (fetchIdRef.current !== myId) return;
        setLogs(data.items || []);
        setTotalCount(data.totalCount ?? 0);
        setTotalPages(data.totalPages ?? 1);
      })
      .catch(e => {
        if (fetchIdRef.current === myId) setError(e?.message || "Failed to load audit logs");
      })
      .finally(() => {
        if (fetchIdRef.current === myId) setLoading(false);
      });
  }, [allowed, page, userFilter, deptFilter, entityTypeFilter, projectFilter, search]);

  // Reset to page 1 on filter change
  useEffect(() => { setPage(1); }, [userFilter, deptFilter, entityTypeFilter, projectFilter, search]);

  const activeFilters = [userFilter, deptFilter, projectFilter, entityTypeFilter, search].filter(Boolean).length;

  function clearFilters() {
    setUserFilter("");
    setDeptFilter("");
    setProjectFilter("");
    setEntityTypeFilter("");
    setSearch("");
    setPage(1);
  }

  function exportCSV() {
    const headers = ["When", "Performed By", "Role", "Department", "Entity Type", "Task / Entity", "Action"];
    const rows = logs.map(l => [
      `"${formatDateTime(l.performedAt)}"`,
      `"${l.performedByUserName || ""}"`,
      l.performedByUserRole || "",
      `"${l.performedByUserDepartmentName || ""}"`,
      l.entityType || "",
      `"${l.taskTitle || l.entityId || ""}"`,
      `"${(l.action || "").replace(/"/g, '""')}"`,
    ]);
    const csv = [headers, ...rows].map(r => r.join(",")).join("\n");
    const a = Object.assign(document.createElement("a"), {
      href: URL.createObjectURL(new Blob([csv], { type: "text/csv" })),
      download: `audit-log-${new Date().toISOString().slice(0, 10)}.csv`,
    });
    a.click();
  }

  if (!allowed) {
    return (
      <div>
        <PageHeader title="Audit Logs" subtitle="CXO / Admin only" />
        <div className="glass-card" style={{ padding: 24 }}>
          You don't have permission to view this page.
        </div>
      </div>
    );
  }

  // Entity type options derived from known types + current page results
  const knownEntityTypes = ["Task", "User", "Department", "Project", "MOM", "Evidence"];

  return (
    <div>
      <PageHeader
        title="Audit Logs"
        subtitle={`${activeFilters > 0 ? `${totalCount} matching` : totalCount} actions · Every entry individually preserved`}
        actions={
          <>
            <button
              onClick={exportCSV}
              disabled={loading || logs.length === 0}
              style={{
                padding: "9px 14px", borderRadius: 10, fontSize: 13, fontWeight: 600,
                background: "var(--surface-2)", color: "var(--text-2)",
                border: "1px solid var(--border-strong)", cursor: "pointer",
                opacity: logs.length === 0 ? 0.5 : 1,
              }}
            >
              ⬇ Export CSV
            </button>
            <button className="btn-ghost" onClick={() => setPage(p => { const r = p; setPage(1); return r === 1 ? r : 1; })} disabled={loading} style={{ padding: "9px 14px", borderRadius: 10 }}>
              {loading ? "Refreshing…" : "Refresh"}
            </button>
          </>
        }
      />

      {error && (
        <div style={{ marginBottom: 16, padding: "10px 14px", borderRadius: 8, background: "var(--danger-soft)", border: "1px solid var(--danger-border)", color: "var(--danger-text)", fontSize: 13 }}>{error}</div>
      )}

      {/* Filter Bar */}
      <div
        style={{
          display: "flex",
          gap: 6,
          flexWrap: "wrap",
          alignItems: "center",
          marginBottom: 14,
          padding: "10px 14px",
          background: "var(--surface)",
          border: "1px solid var(--border)",
          borderRadius: 12,
        }}
      >
        <span
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: "var(--text-3)",
            textTransform: "uppercase",
            letterSpacing: "0.1em",
            marginRight: 2,
          }}
        >
          Filter
        </span>

        {users.length > 0 && (
          <FilterChip
            value={userFilter}
            onChange={setUserFilter}
            label="User"
            options={users.map((u) => ({ value: u.id, label: u.name }))}
          />
        )}
        {departments.length > 0 && (
          <FilterChip
            value={deptFilter}
            onChange={setDeptFilter}
            label="Department"
            options={departments.filter(d => !d.parentDepartmentId).map((d) => ({ value: d.id, label: d.name }))}
          />
        )}
        {projects.length > 0 && (
          <FilterChip
            value={projectFilter}
            onChange={setProjectFilter}
            label="Project"
            options={projects.map((p) => ({ value: p.id, label: p.name }))}
          />
        )}
        <FilterChip
          value={entityTypeFilter}
          onChange={setEntityTypeFilter}
          label="Entity Type"
          options={knownEntityTypes.map((e) => ({ value: e, label: e }))}
        />

        <div style={{ position: "relative", marginLeft: 4 }}>
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search..."
            style={{
              padding: "5px 10px",
              borderRadius: 20,
              fontSize: 12,
              border: search
                ? "1.5px solid var(--brand)"
                : "1px solid var(--border-strong)",
              background: search ? "var(--brand-soft)" : "var(--surface-2)",
              color: "var(--text-1)",
              minWidth: 130,
            }}
          />
        </div>

        {activeFilters > 0 && (
          <>
            <div
              style={{
                width: 1,
                height: 18,
                background: "var(--border-strong)",
                margin: "0 4px",
              }}
            />
            <button
              onClick={clearFilters}
              style={{
                padding: "5px 10px",
                borderRadius: 6,
                fontSize: 12,
                fontWeight: 600,
                background: "var(--danger-bg)",
                color: "var(--danger-text)",
                border: "1px solid #fecdd3",
                display: "flex",
                alignItems: "center",
                gap: 4,
              }}
            >
              <span style={{ fontSize: 10 }}>✕</span> Clear{" "}
              {activeFilters > 1 ? `(${activeFilters})` : ""}
            </button>
          </>
        )}
      </div>

      <div className="glass-card" style={{ padding: 0 }}>
        <div className="tasks-table">
          <div
            className="tasks-row header"
            style={{ gridTemplateColumns: "1.4fr 1.1fr 0.9fr 1.1fr 1.7fr" }}
          >
            <div>When</div>
            <div>Who</div>
            <div>Department</div>
            <div>Entity</div>
            <div>Action</div>
          </div>

          {loading && <Loader.TableRows rows={8} />}

          {!loading && logs.length === 0 && (
            <EmptyState
              icon="🔍"
              title={activeFilters > 0 ? "No logs match your filters" : "No audit logs yet"}
              subtitle={activeFilters > 0 ? "Try adjusting or clearing your filters." : "Activity across the platform will be recorded here."}
            />
          )}

          {!loading &&
            logs.map((l) => (
              <div
                key={l.id}
                className={`tasks-row${l.entityType !== "Task" ? " no-left-accent" : ""}`}
                style={{
                  gridTemplateColumns: "1.4fr 1.1fr 0.9fr 1.1fr 1.7fr",
                  cursor: l.entityType === "Task" ? "pointer" : "default",
                }}
                onClick={() => l.entityType === "Task" && l.entityId && navigate(`/tasks/${l.entityId}`)}
                title={l.entityType === "Task" ? "Click to open task" : undefined}
              >
                <div style={{ color: "var(--text-2)", fontSize: 12 }}>
                  {formatDateTime(l.performedAt)}
                </div>
                <div style={{ color: "var(--text-2)", fontSize: 13, fontWeight: 500 }}>
                  {l.performedByUserName || shortId(l.performedByUserId)}
                  {l.performedByUserRole && (
                    <div style={{ fontSize: 11, color: "var(--text-3)", fontWeight: 400 }}>
                      {l.performedByUserRole}
                    </div>
                  )}
                </div>
                <div style={{ fontSize: 12, color: "var(--text-2)" }}>
                  {l.performedByUserDepartmentName || <span style={{ color: "var(--text-3)" }}>—</span>}
                </div>
                <div style={{ color: "var(--text-2)", fontSize: 12 }}>
                  <span style={{ fontWeight: 600 }}>{l.entityType}</span>
                  <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 1 }}>
                    {l.taskTitle || shortId(l.entityId)}
                  </div>
                </div>
                <div>
                  <ActionBadge action={l.action} />
                </div>
              </div>
            ))}
        </div>
        <Pagination
          page={page}
          totalPages={totalPages}
          onPageChange={(p) => setPage(Math.max(1, Math.min(p, totalPages)))}
          total={totalCount}
          pageSize={PAGE_SIZE}
        />
      </div>
    </div>
  );
}
